<script setup lang="ts">
import { onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { logoutUser } from '@/lib/stytch'
import { useRouter } from 'vue-router'

const auth = useAuthStore()
const router = useRouter()
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

// Redirect active org users away from this page
onMounted(async () => {
  if (!auth.isAuthenticated || !auth.stytchToken) {
    router.replace({ name: 'login' })
    return
  }

  try {
    const response = await fetch(`${BACKEND_URL}/api/homepage`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${auth.stytchToken}` },
    })

    // Org is confirmed active - redirect away
    if (response.ok) {
      router.replace({ name: auth.isAdmin ? 'admin' : 'home' })
      return
    }

    // Stay on page for 403 "Organization is inactive", otherwise stay to be safe
    if (response.status === 403) {
      const data = await response.json()
      if (data?.detail !== 'Organization is inactive') {
        // 403 for other reason - stay on page to be safe
        return
      }
    }
    // Unknown error (5xx, other 4xx) - stay on page to be safe
  } catch {
    // Network error - stay on page to be safe
  }
})

async function handleLogout() {
  try {
    await logoutUser()
  } catch {
    // ignore revocation errors
  }
  auth.clearSession()
  router.replace({ name: 'login' })
}
</script>

<template>
  <UContainer class="flex items-center justify-center min-h-screen">
    <UCard class="max-w-md w-full text-center">
      <template #header>
        <div class="flex justify-center mb-4">
          <UIcon name="i-lucide-building-2" class="w-16 h-16 text-warning" />
        </div>
        <h1 class="text-2xl font-bold">Organization Disabled</h1>
      </template>

      <p class="text-muted mb-6">
        Your organization has been disabled. If you believe this is an error, please contact
        support.
      </p>

      <UButton
        to="mailto:support@ginjer.ai"
        color="primary"
        size="lg"
        class="mb-4 w-full"
        icon="i-lucide-mail"
      >
        Contact support@ginjer.ai
      </UButton>

      <template #footer>
        <UButton color="neutral" variant="ghost" size="sm" @click="handleLogout" class="w-full">
          Sign out
        </UButton>
      </template>
    </UCard>
  </UContainer>
</template>
